'use client';
import { cn } from 'cn';

import type { GetProjectQuery } from '@/survey-graphql';

import SurveyAiFormsAiSectionCompletion from './survey-ai-forms-ai-section-completion';
import SurveyAiFormsAiSectionHeader from './survey-ai-forms-ai-section-header';
import SurveyAiFormsAiSectionQuestionRenderer from './survey-ai-forms-ai-section-question-renderer';
import { useAiSection } from './survey-ai-forms-ai-section.hooks';
import SurveyAiFormsProgressDots from './survey-ai-forms-progress-dots';
import type {
  FormAnswerValue,
  ResponseMode,
  ViewMode,
} from './survey-ai-forms.types';

type ProjectQuestion = NonNullable<
  GetProjectQuery['project']
>['questions'][number];

interface Props {
  currentQuestion: ProjectQuestion | null;
  currentQuestionIndex: number;
  totalQuestions: number;
  questions?: ProjectQuestion[];
  responseMode: ResponseMode;
  onResponseSubmit: (value: FormAnswerValue) => void;
  onNext?: () => void;
  onQuestionFocus?: (questionId: string) => void;
  saveTranscript: (questionId: string, transcript: string) => void;
  getTranscript: (questionId: string) => string;
  viewMode?: ViewMode;
  showInstructions?: boolean;
  onCloseInstructions?: () => void;
  onViewModeToggle?: () => void;
  onSubmit: () => void;
  progress: number;
  estimatedTotalTimeInSeconds: number;
  className?: string;
  isMobile?: boolean;
  isAddingMessage?: boolean;
  isSubmitting?: boolean;
  isProcessingResponse?: boolean;
  canSubmit?: boolean;
  isUserVoiceEnabled?: boolean;
  isPreviewMode?: boolean;
  isAIFormAssistanceEnabled?: boolean;
}

export default function SurveyAiFormsAiSection({
  currentQuestion,
  currentQuestionIndex,
  totalQuestions,
  questions,
  responseMode,
  onResponseSubmit,
  onQuestionFocus,
  saveTranscript,
  getTranscript,
  viewMode = 'ai',
  showInstructions = true,
  onCloseInstructions,
  onViewModeToggle,
  onSubmit,
  progress,
  estimatedTotalTimeInSeconds,
  className = '',
  isMobile = false,
  isSubmitting = false,
  isProcessingResponse = false,
  canSubmit = false,
  isUserVoiceEnabled = false,
  isAIFormAssistanceEnabled = true,
}: Props) {
  // ALWAYS call hooks (Rules of Hooks) - handle null case after
  const {
    transcriptValue,
    setTranscriptValue,
    currentValue,
    openEndedDisplayValue,
    hasValue,
    isFormattingTranscript,
    handleQuestionValueChange,
    handleOpenEndedChange,
    handleSubmit,
    handleAiNext,
  } = useAiSection({
    currentQuestion,
    questions: questions ?? [],
    onResponseSubmit,
    saveTranscript,
    getTranscript,
    responseMode,
  });

  // Combined loading state: disable buttons during API calls
  // Voice: formatTranscriptAnswer + addFormInsight + addFormMessage
  // Non-voice: addFormInsight + addFormMessage
  const isLoading = isFormattingTranscript || isProcessingResponse;

  // Render completion screen when no current question
  if (!currentQuestion) {
    return (
      <div className={className} data-test='ai-section-completion-wrapper'>
        <SurveyAiFormsAiSectionCompletion
          onSubmit={onSubmit}
          totalQuestions={totalQuestions}
          questions={questions}
          onQuestionClick={onQuestionFocus}
          onViewModeToggle={onViewModeToggle}
          viewMode={viewMode}
          progress={progress}
          estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
          isSubmitting={isSubmitting}
          canSubmit={canSubmit}
        />
      </div>
    );
  }

  return (
    <div
      data-test='ai-section-wrapper'
      className={cn(
        'flex size-full scrollbar-none flex-col justify-between overflow-y-auto rounded-4xl px-4 py-8 [-ms-overflow-style:none] sm:px-6 [&::-webkit-scrollbar]:hidden',
        className
      )}
    >
      {/* Header */}
      <SurveyAiFormsAiSectionHeader
        progress={progress}
        estimatedTotalTimeInSeconds={estimatedTotalTimeInSeconds}
        viewMode={viewMode}
        onViewModeToggle={onViewModeToggle}
        showInstructions={showInstructions}
        onCloseInstructions={onCloseInstructions}
        isAIFormAssistanceEnabled={isAIFormAssistanceEnabled}
      />

      {/* Content */}
      <div className='mx-auto flex w-full max-w-180 flex-1 flex-col justify-center'>
        <SurveyAiFormsAiSectionQuestionRenderer
          question={currentQuestion}
          questionValue={currentValue}
          transcriptValue={transcriptValue}
          openEndedDisplayValue={openEndedDisplayValue}
          onQuestionValueChange={handleQuestionValueChange}
          onOpenEndedChange={handleOpenEndedChange}
          onTranscriptChange={setTranscriptValue}
          onSubmit={handleSubmit}
          onNext={handleAiNext}
          hasValue={hasValue}
          responseMode={responseMode}
          loading={isLoading}
          isMobile={isMobile}
          isUserVoiceEnabled={isUserVoiceEnabled}
        />
      </div>

      {/* Footer - Progress Dots */}
      <SurveyAiFormsProgressDots
        currentIndex={currentQuestionIndex}
        total={totalQuestions}
        questions={questions}
        onQuestionClick={onQuestionFocus}
      />
    </div>
  );
}
